import { getState } from '../analytics-state';
import {
  STASH, NOT_APPLICABLE, BIP_PAGE, SHOPPABLE_IMAGES_DRAWER, PIP
} from '../constants';

const publishEvent = ({ eventObject }) => {
  try {
    digitalData.event.push(eventObject);
  } catch (error) {
    console.error('Error in publishEvent()', error);
  }
};

// event containing just primary category and event name is a common pattern in analytics
// so we can create a generic function to handle it
const handleEvent = (primaryCategory, eventName) => {
  try {
    const eventObject = {
      category: {
        primaryCategory
      },
      eventInfo: {
        eventName,
      },
    };
    publishEvent({ eventObject });
  } catch (error) {
    console.error(error);
  }
};

const eventOriginatedFromProductPodOrFbt = (path) => {
  return (path || [])
    .reverse()
    .find((name) => (
      name.match(/ProductPodDefaultMobile\[/i)
      || name.match(/ProductPodDefaultDesktop\[/i)
      || name.match(/ProductPodDesktop\[/i)
      || name.match(/ProductPodMobile\[/i)
      || name.match(/productpod\[/i)
      || name.match(/frequentlyboughttogether\[/i)));
};

const eventOriginatedFromPromotionProducts = (path) => {
  return !!(path || []).find((name) => name.match(/promotionproducts/i));
};

const eventOriginatedFromApplianceBundles = (path) => {
  return !!(path || []).find((name) => name.match(/ApplianceBundles\[/i));
};

const isFromPackagePageOrPackageMini = (path) => {
  const packagePageRE = /packagepage/i;
  const packageMiniRE = /packagesmini/i;
  const isFromPackages = path.find((part = '') => (packagePageRE.test(part) || packageMiniRE.test(part)));
  return !!isFromPackages;
};

// The gift card PIP renders its Add To Cart inside GiftCardsPage with no buybox in the path.
// Scoped to the PIP URL so other GiftCardsPage views (mall, balance/reload) are not reported as a PIP buybox
const isFromGiftCardPip = (path) => {
  const isGiftCardPipUrl = /^\/gift-cards\/p\//.test(window?.location?.pathname || '');
  return isGiftCardPipUrl && !!(path || []).find((part = '') => /^GiftCardsPage\[/i.test(part));
};

// The gift card carousel on /c/gift-cards opens a "Gift Card Selections" drawer with its own Add To Cart.
// Scoped to that URL so a Drawer/GiftCardLayout path on another route keeps its existing section and pageType
const isFromGiftCardSelectionsDrawer = (path) => {
  const isGiftCardsContentUrl = /^\/c\/gift-cards\/?$/i.test(window?.location?.pathname || '');
  const parts = path || [];
  return isGiftCardsContentUrl
    && !!parts.find((part = '') => /^Drawer\[/.test(part))
    && !!parts.find((part = '') => /^GiftCardLayout\[/.test(part));
};

// Route state reports /gift-cards/ as 'giftcard' and /c/ as 'content', so gift card adds take their page type
// from the page itself: the gift card PIP is a PIP, and the selections drawer uses the page's data layer type
const getStashPageType = (path, pageType) => {
  if (isFromGiftCardPip(path)) return PIP;
  if (isFromGiftCardSelectionsDrawer(path)) return digitalData?.page?.pageInfo?.pageType || pageType;
  return pageType;
};

export const getSectionFromPath = ({ path, cartOptions }) => {
  const buyBoxRE = /buybox/i;
  const sampleBoxRE = /sample/i;
  const projectsRE = /projectDetails/i;
  const isFromBuyBox = (path || []).find((part = '') => buyBoxRE.test(part));
  const isSampleBox = (path || []).find((part = '') => sampleBoxRE.test(part));
  const isFromPackages = isFromPackagePageOrPackageMini(path);
  const isFromProjects = (path || []).find((part = '') => projectsRE.test(part));

  if (eventOriginatedFromPromotionProducts(path)) {
    return 'module';
  }
  if (isFromBuyBox || isFromGiftCardPip(path)) {
    return 'buybox';
  }
  if (isFromGiftCardSelectionsDrawer(path)) {
    return 'gift card selections';
  }
  if (isSampleBox) {
    return 'sample';
  }
  if (isFromPackages) {
    return cartOptions?.packageInfo?.packageName || 'n/a';
  }
  if (cartOptions?.component === SHOPPABLE_IMAGES_DRAWER) {
    return 'n/a';
  }
  if (isFromProjects) {
    return cartOptions?.section || 'n/a';
  }
  return 'unknown';
};

export const getTargetFromPath = ({ path }) => {
  const lowerCasePath = path.map((name) => (name || '').toLowerCase());
  const atcIndex = lowerCasePath.findIndex((part) => /^addtocart/i.test(part));
  return atcIndex > -1 ? 'atc' : '';
};

const getItemsFromCartReqParams = (cartReqParams, cartOptions, path) => {
  const state = getState();
  const recommendation = {
    recommendationType: cartOptions?.recommendationType || cartOptions?.sharedSection || NOT_APPLICABLE,
    pageType: state?.pageType,
    displayPosition: NOT_APPLICABLE,
    strategy: NOT_APPLICABLE,
    recommendationVersion: NOT_APPLICABLE,
    featureVersion: NOT_APPLICABLE,
    anchorProductSku: cartOptions?.anchorProductSku || NOT_APPLICABLE,
  };
  const isFromPackages = isFromPackagePageOrPackageMini(path);

  if (Array.isArray(cartReqParams)) {
    if (isFromPackages) {
      return cartReqParams.map(({ itemId, displayPosition = NOT_APPLICABLE }) => (
        {
          productInfo: {
            sku: itemId,
          },
          displayPosition,
          recommendation
        }
      ));
    }
    return cartReqParams.map(({ itemId, displayPosition }) => (
      {
        productInfo: {
          sku: itemId,
        },
        displayPosition,
      }
    ));
  }
  return cartOptions?.component === SHOPPABLE_IMAGES_DRAWER
    ? [{ productInfo: { sku: cartReqParams?.itemId }, index: 'n/a' }]
    : [{ productID: cartReqParams?.itemId, index: '' }];
};

export const addToCartAnalytics = () => {
  // @todo logic for dpd atc click
  // https://github.homedepot.com/thd-olt-component/hdhome-analytics/blob/master/events/dpd.js#L35

  window.LIFE_CYCLE_EVENT_BUS.on('cart.add-to-cart', ({ output }) => {
    const {
      cartReqParams,
      cartOptions,
      path = []
    } = output;

    if (
      (eventOriginatedFromProductPodOrFbt(path) && !eventOriginatedFromPromotionProducts(path))
      || eventOriginatedFromApplianceBundles(path) || cartOptions?.disableATCAnalytics
    ) return;

    const { pageType = '' } = getState() || {};

    digitalData.event = digitalData.event || [];
    if (pageType !== 'browse') {
      const section = getSectionFromPath({ path, cartOptions });
      const target = getTargetFromPath({ path });
      const items = getItemsFromCartReqParams(cartReqParams, cartOptions, path);
      const isSample = cartOptions?.section === 'order sample';
      const isBipPage = pageType === BIP_PAGE;
      const sharedSection = cartOptions?.sharedSection || 'n/a';
      const component = cartOptions?.component || '';
      const eventName = component === SHOPPABLE_IMAGES_DRAWER || (Array.isArray(cartReqParams) && !isSample && !isBipPage)
        ? 'cart addition info'
        : 'cart add location';

      const ddoStashEvent = {
        eventInfo: {
          eventName
        },
        category: {
          primaryCategory: STASH
        },
        eventMode: STASH,
        stash: {
          component,
          item: items,
          pageType: getStashPageType(path, pageType),
          section,
          sharedSection,
          target
        }
      };
      digitalData.event.push(ddoStashEvent);
    }
  });
};

export const quickAddToCartLocation = () => {
  window.LIFE_CYCLE_EVENT_BUS.on('cart.quick-add-location', ({ output }) => {
    const { data } = output;

    const ddoStashEvent = {
      category: {
        primaryCategory: STASH
      },
      eventInfo: {
        eventName: 'cart add location',
      },
      eventMode: STASH,
      stash: {
        component: data.component || 'n/a',
        pageType: data.pageType || 'quick add',
        section: data.section || 'quick add',
        sharedSection: 'n/a',
        target: data.target || 'atc',
      }
    };
    digitalData.event.push(ddoStashEvent);
  });
};

export const quickAddToCartClick = () => {
  window.LIFE_CYCLE_EVENT_BUS.on('cart.quick-add-click', ({ output }) => {
    const { data } = output;

    // handle stash event
    const ddoStashEvent = {
      category: {
        primaryCategory: data.primaryCategory
      },
      eventInfo: {
        eventName: data.eventName,
      },
      cart: {
        cartID: data.cartObjectData?.cartID
      },
      item: data.items || []
    };
    digitalData.event.push(ddoStashEvent);

    // update cart Object
    const cartObjItems = data.cartObjectData?.items || [];
    const cartObj = {
      ...digitalData.cart,
      cartID: data.cartObjectData?.cartID,
      price: data.cartObjectData?.cartTotal,
      uniqueSkuCount: data.cartObjectData?.uniqueSkuCount,
      quantityTotal: data.cartObjectData?.quantityTotal,
    };

    if (digitalData?.cart?.item && Array.isArray(digitalData?.cart?.item)) {
      // Overwrite existing cart object properties and push new item to it
      digitalData.cart = { ...digitalData.cart, ...cartObj };
      digitalData.cart.item.push(...cartObjItems);
      return;
    }
    digitalData.cart = { ...cartObj, item: cartObjItems };
  });
};

export const subscriptionAcknowledgementAnalytics = () => {
  window.LIFE_CYCLE_EVENT_BUS.on('cart.subscription-acknowledgement', () => {
    handleEvent('button submission', 'subscription details acceptance');
  });
};
